import { Schema, type Node as PmNode, type Mark } from "prosemirror-model";
import type { RichTextDoc, InlineNode, Paragraph, BlockNode, TextMarks } from "@broadpaper/core";

/** ProseMirror schema mirroring the BroadPaper rich text model exactly. */
export const richTextSchema = new Schema({
  nodes: {
    doc: { content: "block+" },
    paragraph: {
      group: "block",
      content: "inline*",
      attrs: { align: { default: null } },
      parseDOM: [{ tag: "p", getAttrs: (dom) => ({ align: (dom as HTMLElement).style.textAlign || null }) }],
      toDOM: (n) => ["p", { class: "bp-p", style: n.attrs.align ? `text-align:${n.attrs.align}` : undefined }, 0]
    },
    bullet_list: { group: "block", content: "list_item+", parseDOM: [{ tag: "ul" }], toDOM: () => ["ul", { class: "bp-list" }, 0] },
    ordered_list: { group: "block", content: "list_item+", parseDOM: [{ tag: "ol" }], toDOM: () => ["ol", { class: "bp-list" }, 0] },
    list_item: { content: "paragraph+", parseDOM: [{ tag: "li" }], toDOM: () => ["li", 0], defining: true },
    text: { group: "inline" },
    hard_break: { group: "inline", inline: true, selectable: false, parseDOM: [{ tag: "br" }], toDOM: () => ["br"] },
    token: {
      group: "inline",
      inline: true,
      atom: true,
      selectable: true,
      draggable: true,
      attrs: { expr: {}, label: { default: null } },
      parseDOM: [{ tag: "span[data-expr]", getAttrs: (dom) => ({ expr: (dom as HTMLElement).getAttribute("data-expr"), label: (dom as HTMLElement).textContent }) }],
      toDOM: (n) => ["span", { class: "bp-token", "data-expr": n.attrs.expr, title: n.attrs.expr, contenteditable: "false" }, n.attrs.label ?? n.attrs.expr]
    }
  },
  marks: {
    bold: { parseDOM: [{ tag: "strong" }, { tag: "b" }, { style: "font-weight=bold" }], toDOM: () => ["strong", 0] },
    italic: { parseDOM: [{ tag: "em" }, { tag: "i" }, { style: "font-style=italic" }], toDOM: () => ["em", 0] },
    underline: { parseDOM: [{ tag: "u" }, { style: "text-decoration=underline" }], toDOM: () => ["u", 0] },
    strike: { parseDOM: [{ tag: "s" }, { tag: "del" }, { style: "text-decoration=line-through" }], toDOM: () => ["s", 0] },
    code: { parseDOM: [{ tag: "code" }], toDOM: () => ["code", { class: "bp-code" }, 0] },
    link: { attrs: { href: {} }, inclusive: false, parseDOM: [{ tag: "a[href]", getAttrs: (dom) => ({ href: (dom as HTMLElement).getAttribute("href") }) }], toDOM: (m) => ["a", { href: m.attrs.href, class: "bp-link" }, 0] },
    color: { attrs: { color: {} }, parseDOM: [{ style: "color", getAttrs: (v) => ({ color: v }) }], toDOM: (m) => ["span", { style: `color:${m.attrs.color}` }, 0] }
  }
});

/* ---------------------------- model → PM ---------------------------- */

function marksToPm(marks: TextMarks | undefined): Mark[] {
  if (!marks) return [];
  const out: Mark[] = [];
  const s = richTextSchema.marks;
  if (marks.bold) out.push(s.bold!.create());
  if (marks.italic) out.push(s.italic!.create());
  if (marks.underline) out.push(s.underline!.create());
  if (marks.strike) out.push(s.strike!.create());
  if (marks.code) out.push(s.code!.create());
  if (marks.link) out.push(s.link!.create({ href: marks.link }));
  if (marks.color) out.push(s.color!.create({ color: marks.color }));
  return out;
}

function inlineToPm(content: InlineNode[], labelFor: (expr: string) => string): PmNode[] {
  const out: PmNode[] = [];
  for (const n of content) {
    if (n.type === "text") {
      if (n.text) out.push(richTextSchema.text(n.text, marksToPm(n.marks)));
    } else if (n.type === "token") out.push(richTextSchema.nodes.token!.create({ expr: n.expr, label: labelFor(n.expr) }, undefined, marksToPm(n.marks)));
    else out.push(richTextSchema.nodes.hard_break!.create());
  }
  return out;
}

function paragraphToPm(p: Paragraph, labelFor: (expr: string) => string): PmNode {
  return richTextSchema.nodes.paragraph!.create({ align: p.align ?? null }, inlineToPm(p.content, labelFor));
}

export function modelToPm(doc: RichTextDoc | undefined, labelFor: (expr: string) => string): PmNode {
  const blocks: PmNode[] = [];
  for (const b of doc?.content ?? []) {
    if (b.type === "paragraph") blocks.push(paragraphToPm(b, labelFor));
    else {
      const items = b.content.map((li) => richTextSchema.nodes.list_item!.create(null, li.content.map((p) => paragraphToPm(p, labelFor))));
      blocks.push(richTextSchema.nodes[b.type === "bulletList" ? "bullet_list" : "ordered_list"]!.create(null, items.length ? items : [richTextSchema.nodes.list_item!.create(null, [richTextSchema.nodes.paragraph!.create()])]));
    }
  }
  if (blocks.length === 0) blocks.push(richTextSchema.nodes.paragraph!.create());
  return richTextSchema.nodes.doc!.create(null, blocks);
}

/* ---------------------------- PM → model ---------------------------- */

function pmMarks(marks: readonly Mark[]): TextMarks | undefined {
  if (!marks.length) return undefined;
  const out: TextMarks = {};
  for (const m of marks) {
    switch (m.type.name) {
      case "bold":
        out.bold = true;
        break;
      case "italic":
        out.italic = true;
        break;
      case "underline":
        out.underline = true;
        break;
      case "strike":
        out.strike = true;
        break;
      case "code":
        out.code = true;
        break;
      case "link":
        out.link = String(m.attrs.href ?? "");
        break;
      case "color":
        out.color = String(m.attrs.color ?? "");
        break;
    }
  }
  return Object.keys(out).length ? out : undefined;
}

function pmInline(p: PmNode): InlineNode[] {
  const out: InlineNode[] = [];
  p.forEach((child) => {
    const marks = pmMarks(child.marks);
    if (child.type.name === "text") out.push({ type: "text", text: child.text ?? "", ...(marks ? { marks } : {}) });
    else if (child.type.name === "token") out.push({ type: "token", expr: String(child.attrs.expr), ...(marks ? { marks } : {}) });
    else if (child.type.name === "hard_break") out.push({ type: "break" });
  });
  return out;
}

function pmParagraph(p: PmNode): Paragraph {
  const para: Paragraph = { type: "paragraph", content: pmInline(p) };
  if (p.attrs.align) para.align = p.attrs.align;
  return para;
}

export function pmToModel(doc: PmNode): RichTextDoc {
  const content: BlockNode[] = [];
  doc.forEach((b) => {
    if (b.type.name === "paragraph") content.push(pmParagraph(b));
    else if (b.type.name === "bullet_list" || b.type.name === "ordered_list") {
      const items: Array<{ type: "listItem"; content: Paragraph[] }> = [];
      b.forEach((li) => {
        const ps: Paragraph[] = [];
        li.forEach((p) => {
          if (p.type.name === "paragraph") ps.push(pmParagraph(p));
        });
        items.push({ type: "listItem", content: ps });
      });
      content.push({ type: b.type.name === "bullet_list" ? "bulletList" : "orderedList", content: items });
    }
  });
  return { type: "doc", content: content.length ? content : [{ type: "paragraph", content: [] }] };
}
